/**
 * /dev/components — A2 primitive gallery.
 * Dev-only scaffolding; removed at A-final cutover.
 */
import { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  IconButton,
  ConsolePanel,
  Badge,
  Avatar,
  TextField,
  Textarea,
  Switch,
  Tabs,
  Popover,
  Dialog,
  ToastProvider,
  useToast,
} from '@/ui';
import { Icon } from '@/icons';
import { usePrefsStore } from '@/core/stores/prefsStore';

/* ─── Layout chrome ─────────────────────────────────────────────────── */

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.color.bgRoot};
  padding: 32px 24px 80px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 780px;
`;

const SectionHeading = styled.h2`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  gap: 8px;
`;

const IconCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
`;

const IconLabel = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: 9px;
  color: ${({ theme }) => theme.color.textMuted};
  text-align: center;
  word-break: break-all;
`;

const Code = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Anchor styled like a secondary button — used for cross-panel navigation. */
const SecondaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  transition: opacity 0.12s;
  &:hover { opacity: 0.85; }
`;

/* ─── Toast demo (must be INSIDE ToastProvider) ─────────────────────── */

function ToastDemo() {
  const toast = useToast();
  return (
    <Button variant="secondary" onClick={() => toast('Action completed successfully')}>
      <Icon name="sparkles" size={16} />
      Fire toast
    </Button>
  );
}

/* ─── Tabs demo ──────────────────────────────────────────────────────── */

const TAB_ITEMS = [
  { value: 'posts', label: 'Posts' },
  { value: 'replies', label: 'Replies' },
  { value: 'media', label: 'Media' },
];

function TabsDemo() {
  const [tab, setTab] = useState('posts');
  return (
    <Column>
      <Tabs items={TAB_ITEMS} value={tab} onChange={setTab} />
      <Code>Selected: {tab}</Code>
    </Column>
  );
}

/* ─── Switch demo ────────────────────────────────────────────────────── */

function SwitchDemo() {
  const [on, setOn] = useState(true);
  const [off, setOff] = useState(false);
  return (
    <Row>
      <Switch checked={on} onChange={setOn} label="Show likes" />
      <Code>{on ? 'on' : 'off'}</Code>
      <Switch checked={off} onChange={setOff} label="Auto-play" />
      <Code>{off ? 'on' : 'off'}</Code>
      <Switch checked={false} onChange={() => {}} label="Disabled switch" disabled />
      <Code>disabled</Code>
    </Row>
  );
}

/* ─── TextField demo ─────────────────────────────────────────────────── */

function TextFieldDemo() {
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('not-an-email');
  return (
    <Column>
      <TextField
        label="Handle"
        placeholder="@username"
        value={handle}
        onChange={(e) => setHandle(e.target.value)}
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={email && !email.includes('@') ? 'Enter a valid email address' : undefined}
      />
    </Column>
  );
}

/* ─── Textarea demo ──────────────────────────────────────────────────── */

function TextareaDemo() {
  const [bio, setBio] = useState('');
  return (
    <Textarea
      label="Bio"
      placeholder="Tell your story…"
      value={bio}
      onChange={(e) => setBio(e.target.value)}
      maxLength={160}
    />
  );
}

/* ─── Popover demo ───────────────────────────────────────────────────── */

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  text-align: left;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
`;

function PopoverDemo() {
  return (
    <Popover
      trigger={
        <Button variant="secondary">
          <Icon name="more-horizontal" size={16} />
          Options
        </Button>
      }
    >
      <Column style={{ gap: 2 }}>
        <MenuItem type="button">
          <Icon name="bookmark" size={16} />
          Bookmark
        </MenuItem>
        <MenuItem type="button">
          <Icon name="share" size={16} />
          Share
        </MenuItem>
        <MenuItem type="button">
          <Icon name="shield" size={16} />
          Report
        </MenuItem>
      </Column>
    </Popover>
  );
}

/* ─── Dialog demo ────────────────────────────────────────────────────── */

function DialogDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={16} />
        Open dialog
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Confirm action">
        <Column>
          <p style={{ margin: 0, color: 'inherit' }}>
            This is a focusable dialog with a focus trap. Press Tab to cycle through focusable
            elements, Escape to close, or click the backdrop.
          </p>
          <Row>
            <Button onClick={() => setOpen(false)}>Confirm</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Row>
        </Column>
      </Dialog>
    </>
  );
}

/* ─── The gallery page itself ────────────────────────────────────────── */

function GalleryContent() {
  const { themeMode, toggleTheme } = usePrefsStore();

  return (
    <Page>
      <PageHeader>
        <PageTitle>
          <Icon name="logo-x" size={28} /> Component Gallery
        </PageTitle>
        <Button variant="secondary" onClick={toggleTheme}>
          <Icon name={themeMode === 'dark' ? 'sun' : 'moon'} size={16} />
          {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
        </Button>
      </PageHeader>

      <Sections>
        {/* ── Dev panel links ─────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Dev panels</SectionHeading>
          <Row>
            <SecondaryLink href="/dev/personalization">
              <Icon name="sparkles" size={16} />
              Personalization + Session panel
            </SecondaryLink>
          </Row>
        </ConsolePanel>

        {/* ── Buttons ────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Button: 4 variants × 2 sizes + disabled</SectionHeading>
          <Column>
            <Row>
              <Button variant="primary" size="md">Primary md</Button>
              <Button variant="secondary" size="md">Secondary md</Button>
              <Button variant="ghost" size="md">Ghost md</Button>
              <Button variant="danger" size="md">Danger md</Button>
            </Row>
            <Row>
              <Button variant="primary" size="sm">Primary sm</Button>
              <Button variant="secondary" size="sm">Secondary sm</Button>
              <Button variant="ghost" size="sm">Ghost sm</Button>
              <Button variant="danger" size="sm">Danger sm</Button>
            </Row>
            <Row>
              <Button variant="primary" disabled>Disabled primary</Button>
              <Button variant="secondary" disabled>Disabled secondary</Button>
            </Row>
          </Column>
        </ConsolePanel>

        {/* ── IconButton ──────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>IconButton</SectionHeading>
          <Row>
            <IconButton icon="bell" label="Notifications" />
            <IconButton icon="bookmark" label="Bookmarks" />
            <IconButton icon="settings" label="Settings" />
            <IconButton icon="search" label="Search" />
            <IconButton icon="mail" label="Messages" />
            <IconButton icon="close" label="Close" />
          </Row>
        </ConsolePanel>

        {/* ── Icon grid ───────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Icon: lucide + brand marks (size 20 + 32)</SectionHeading>
          <Column>
            <IconGrid>
              {(
                [
                  'logo-x',
                  'cherish',
                  'spread-it',
                  'verify',
                  'home',
                  'search',
                  'bell',
                  'bookmark',
                  'heart',
                  'comment',
                  'share',
                  'shield',
                  'sparkles',
                  'users',
                  'plus',
                  'check',
                ] as const
              ).map((name) => (
                <IconCell key={name}>
                  <Icon name={name} size={20} />
                  <IconLabel>{name}</IconLabel>
                </IconCell>
              ))}
            </IconGrid>
            <Row>
              <Icon name="logo-x" size={32} title="TruXSocial logo (large)" />
              <Icon name="cherish" size={32} title="Cherish (large)" />
              <Icon name="spread-it" size={32} title="Spread It (large)" />
              <Icon name="verify" size={32} title="Verified (large)" />
            </Row>
          </Column>
        </ConsolePanel>

        {/* ── ConsolePanel ──────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>ConsolePanel: default + elevated</SectionHeading>
          <Row>
            <ConsolePanel style={{ flex: 1 }}>
              <Code>default</Code>
            </ConsolePanel>
            <ConsolePanel $elevated style={{ flex: 1 }}>
              <Code>$elevated</Code>
            </ConsolePanel>
          </Row>
        </ConsolePanel>

        {/* ── Badge ───────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Badge: all 5 tones</SectionHeading>
          <Row>
            <Badge tone="neutral">Neutral</Badge>
            <Badge tone="cyan">Cyan</Badge>
            <Badge tone="magenta">Under Review</Badge>
            <Badge tone="amber">Amber</Badge>
            <Badge tone="brand">Brand</Badge>
          </Row>
        </ConsolePanel>

        {/* ── Avatar ──────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Avatar: sizes 24 / 32 / 40 / 56 / 72</SectionHeading>
          <Row>
            <Avatar name="WayneTech" size={24} />
            <Avatar name="Alice Johnson" size={32} />
            <Avatar name="Bob Marley" size={40} />
            <Avatar name="Deleted User" size={56} />
            <Avatar name="TruXSocial" size={72} />
          </Row>
        </ConsolePanel>

        {/* ── TextField + Textarea ────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>TextField (normal + error) + Textarea</SectionHeading>
          <TextFieldDemo />
          <div style={{ marginTop: 12 }}>
            <TextareaDemo />
          </div>
        </ConsolePanel>

        {/* ── Switch ──────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Switch: on / off / disabled</SectionHeading>
          <SwitchDemo />
        </ConsolePanel>

        {/* ── Tabs ────────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Tabs: 3 tabs, arrow-key nav</SectionHeading>
          <TabsDemo />
        </ConsolePanel>

        {/* ── Popover ─────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Popover: click to open, Esc / outside to close</SectionHeading>
          <PopoverDemo />
        </ConsolePanel>

        {/* ── Dialog ──────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Dialog: focus trap, Esc + backdrop close</SectionHeading>
          <DialogDemo />
        </ConsolePanel>

        {/* ── Toast ───────────────────────────────────────────────────── */}
        <ConsolePanel>
          <SectionHeading>Toast: fires from useToast()</SectionHeading>
          <ToastDemo />
        </ConsolePanel>
      </Sections>
    </Page>
  );
}

/* ─── Exported page: wraps in ToastProvider so useToast() works ─────── */

export function ComponentsGallery() {
  return (
    <ToastProvider>
      <GalleryContent />
    </ToastProvider>
  );
}
